import { useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import {
  addCartItem, clearCart, getCart, removeCartItem, updateCartItem,
  type CartDto,
} from '@/api/cart';
import { CartContext, type CartContextValue } from './CartContext';

export function CartProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const enabled = !!user;

  const cartQ = useQuery<CartDto>({
    queryKey: ['cart'],
    queryFn: getCart,
    enabled,
    staleTime: 15_000,
  });

  const setCartData = (next: CartDto) => qc.setQueryData(['cart'], next);

  const addMut = useMutation({
    mutationFn: ({ itemId, qty }: { itemId: number; qty: number }) =>
      addCartItem(itemId, qty),
    onSuccess: setCartData,
  });
  const setQtyMut = useMutation({
    mutationFn: ({ itemId, qty }: { itemId: number; qty: number }) =>
      updateCartItem(itemId, qty),
    onSuccess: setCartData,
  });
  const removeMut = useMutation({
    mutationFn: (itemId: number) => removeCartItem(itemId),
    onSuccess: setCartData,
  });
  const clearMut = useMutation({
    mutationFn: () => clearCart(),
    onSuccess: setCartData,
  });

  const cart = cartQ.data ?? null;
  const count = cart?.items.reduce((s, it) => s + it.qty, 0) ?? 0;

  const value: CartContextValue = useMemo(
    () => ({
      cart,
      count,
      isLoading: cartQ.isFetching,
      async add(itemId, qty) { await addMut.mutateAsync({ itemId, qty }); },
      async setQty(itemId, qty) { await setQtyMut.mutateAsync({ itemId, qty }); },
      async remove(itemId) { await removeMut.mutateAsync(itemId); },
      async clear() { await clearMut.mutateAsync(); },
    }),
    // Deliberately omit mutation objects — their identities are stable enough here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cart, count, cartQ.isFetching],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
